import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import * as actions from "../actions";
import ApiProduct from "../components/apiProduct/apiProduct";
import Page404 from "../components/404/404";
import { history } from "../store/store";
import withTracker from "./withtracker";
import AuthorizationHOC from "./AuthorizationHOC";
import { ALL_USERS } from "../constants/userGroups";
import withBreadCrumb from "./withBreadcrumb";
import { useLocation } from "react-router-dom";
import { getUserIdFromState } from "../mappers/backendMappers";
import APIProductService from "../services/APIProductService";
import {
  aliasToRouteParam,
  familyRedirectFor,
  findProductByAlias,
  findProductByFamilyAndVersion,
  productLink,
  resolveLatestActiveVersionForFamily,
} from "../utils/resolveProductFamily";

const ApiProductHOC = (props) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [apiProduct, setApiProduct] = useState(null);
  const [newVersionUrl, setNewVersionUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const changelogFetchedFor = useRef(null);
  // Guards the family-URL redirect below so a resolved family slug only pushes the
  // canonical version-specific URL once, not on every render.
  const familyRedirectedFor = useRef(null);
  const { userId, csrfToken } = props;

  const { role, subscribeErrorMessage, subscribedSuccessfully, breadcrumb } =
    props;
  const { viewMode } = props.match.params;

  const overviewDisplay =
    apiProduct && apiProduct.overviewDisplay && viewMode !== "documentation";

  const fetchSubscriptionStatus = (apiProductUuid) => {
    props.fetchSubscriptionStatus(apiProductUuid);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    props.clearSubscribeErrorMessage();
  };

  // Fetch product using APIProductService
  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      setError(null);

      try {
        // Extract identifier from URL path (could be UUID or urlAlias)
        const pathParts = location.pathname.split("/");
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        // Find the identifier after /apiProduct/
        const apiProductIndex = pathParts.findIndex(
          (part) => part === "apiProduct"
        );
        const identifier =
          apiProductIndex >= 0 && pathParts[apiProductIndex + 1]
            ? pathParts[apiProductIndex + 1]
            : null;

        if (!identifier) {
          setError("Invalid product ID in URL");
          return;
        }

        let productUuid = null;

        // Check if identifier is a UUID
        if (uuidRegex.test(identifier)) {
          productUuid = identifier;
        } else {
          // 1. Try Redux first
          const reduxProducts = props.allProducts || [];

          let matchingProduct = findProductByAlias(reduxProducts, identifier);

          // 2. Fallback to APIProductService in-memory cache
          if (!matchingProduct) {
            const storedProducts =
              APIProductService.getStoredProductList() || [];

            matchingProduct = findProductByAlias(storedProducts, identifier);
          }

          // 3. Final fallback: fetch product list if needed
          if (!matchingProduct) {
            await APIProductService.getProductList(userId, csrfToken);

            const refreshedProducts =
              APIProductService.getStoredProductList() || [];

            matchingProduct = findProductByAlias(refreshedProducts, identifier);
          }

          // 3b. A current version opened by "<family>-v<version>" when that version is
          // stored under another alias (prod keeps "/apiProduct/Bill-Payments" for v3.0)
          if (!matchingProduct) {
            matchingProduct = findProductByFamilyAndVersion(
              (props.allProducts && props.allProducts.length > 0
                ? props.allProducts
                : APIProductService.getStoredProductList()) || [],
              identifier
            );
          }

          // 4. Family URL fallback: the identifier isn't an exact urlAlias for any
          // version, but may be the version-less family link or a product name/slug -
          // resolve to that family's current latest non-deprecated version.
          if (!matchingProduct) {
            const allKnownProducts =
              (props.allProducts && props.allProducts.length > 0
                ? props.allProducts
                : APIProductService.getStoredProductList()) || [];

            matchingProduct = resolveLatestActiveVersionForFamily(
              allKnownProducts,
              identifier
            );
          }

          // 5. Deprecated-inclusive fallback: the identifier isn't an exact urlAlias for any
          // current product and doesn't match a family either - it may be a deprecated
          // product's own real alias (a direct link/bookmark from before it was superseded).
          // The list-based lookups above can never find it since the public list deliberately
          // excludes deprecated products - this is the only path that can. Found via E2E
          // testing 2026-09-20 - see devportal-manager-cms-feature memory.
          // Migrated urlAliases are mostly stored as "/apiProduct/<alias>" (a few bare), and
          // the lookup matches the stored value, so try the prefixed form first, then as typed.
          if (!matchingProduct) {
            const bareAlias = aliasToRouteParam(identifier);
            const aliasCandidates = [`/apiProduct/${bareAlias}`, bareAlias];
            for (const candidate of aliasCandidates) {
              try {
                const resolved = await APIProductService.getProductByUrlAlias(
                  candidate,
                  userId,
                  csrfToken
                );
                if (resolved?.mapped) {
                  matchingProduct = resolved.mapped;
                  break;
                }
              } catch (resolveErr) {
                // Not found under this form - try the next, then fall through to the
                // "Product not found" error.
              }
            }
          }

          if (matchingProduct) {
            productUuid =
              matchingProduct.uuid ||
              matchingProduct.id ||
              matchingProduct.Id ||
              null;

            // A current product settles on its version-less family link (".../Bill-Payments"
            // for "Bill-Payments-v3.0" or an old Drupal alias); a deprecated one, or an older
            // version that is still current, keeps the URL it was opened with.
            const knownProducts =
              (props.allProducts && props.allProducts.length > 0
                ? props.allProducts
                : APIProductService.getStoredProductList()) || [];
            const familyUrl = familyRedirectFor(
              knownProducts,
              matchingProduct,
              identifier
            );
            if (familyUrl && familyRedirectedFor.current !== identifier) {
              familyRedirectedFor.current = identifier;
              // Keep a sub-view such as "/documentation" and any query string or hash
              const subPath = pathParts.slice(apiProductIndex + 2).join("/");
              history.replace(
                `${familyUrl}${subPath ? `/${subPath}` : ""}${
                  location.search || ""
                }${location.hash || ""}`
              );
              return;
            }
          } else {
            setError("Product not found");
            return;
          }
        }

        if (productUuid) {
          const productData = await APIProductService.getProductDocument(
            productUuid,
            userId,
            csrfToken
          );

          if (productData && productData.mapped) {
            setApiProduct(productData.mapped);
            // Fetch subscription status
            fetchSubscriptionStatus(productUuid);
            // Trigger changelog fetch only once per product
            if (changelogFetchedFor.current !== productUuid) {
              changelogFetchedFor.current = productUuid;
              // Note: Don't call fetchApiProductSuccess here as it re-parses with old parser
              // and breaks the data. The changelog fetch is handled separately.
              // props.fetchApiProductSuccess(productData.original);
            }

            // Deprecated product: resolve the family's current latest version so the
            // "GO TO NEW VERSION" banner has somewhere real to link to (this used to call
            // a dead legacy Drupal endpoint and never rendered - see
            // devportal-manager-cms-feature memory).
            if (productData.mapped.deprecated && productData.mapped.title) {
              // A direct/UUID navigation to a single deprecated product's page never runs the
              // alias-resolution branch above, so the product list can still be empty here even
              // though this is a separate check - force a fetch rather than trust whatever
              // happens to already be cached. Found via E2E testing 2026-09-20.
              let productListForFamily =
                (props.allProducts && props.allProducts.length > 0
                  ? props.allProducts
                  : APIProductService.getStoredProductList()) || [];
              if (productListForFamily.length === 0) {
                await APIProductService.getProductList(userId, csrfToken);
                productListForFamily =
                  APIProductService.getStoredProductList() || [];
              }
              const latest = resolveLatestActiveVersionForFamily(
                productListForFamily,
                productData.mapped.title
              );
              const latestAlias =
                latest && (latest.alias || latest.urlAlias || latest.UrlAlias);
              setNewVersionUrl(
                latestAlias ? productLink(latestAlias, false) : null
              );
            } else {
              setNewVersionUrl(null);
            }
          }
        } else {
          setError("Invalid product ID in URL");
        }
      } catch (err) {
        console.error("Error loading product:", err);
        setError(err.message);
        setApiProduct(null);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
    // Note: allProducts is intentionally NOT in dependencies to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, userId, csrfToken]);

  useEffect(() => {
    return () => {
      // Clear service cache when component unmounts
      APIProductService.clear();
    };
  }, []);

  const overviewDisplayToggler = () => {
    history.push(props.location.pathname + "/documentation");
  };

  // changeLog is already parsed onto apiProduct by parseApiProduct - it's embedded
  // on the product document itself, not a separate fetch. See devportal-manager-cms-feature memory.

  // An unknown product gets the portal's 404 page rather than a bare error line
  if (error === "Product not found" && !loading) {
    return <Page404 />;
  }

  // Show error if product failed to load
  if (error && !loading) {
    return <div className="error-message">Error loading product: {error}</div>;
  }

  return (
    <ApiProduct
      breadcrumb={breadcrumb}
      overviewDisplay={overviewDisplay}
      overviewDisplayToggler={overviewDisplayToggler}
      apiProduct={apiProduct}
      changeLog={apiProduct?.changeLog}
      newVersionUrl={newVersionUrl}
      loading={loading}
      role={role}
      openModal={() => setModalIsOpen(true)}
      modalIsOpen={modalIsOpen}
      closeModal={closeModal}
      subscribeErrorMessage={subscribeErrorMessage}
      subscribedSuccessfully={subscribedSuccessfully}
    />
  );
};

const mapStateToProps = (state) => {
  // Get all products as an array for alias lookup
  const allProducts = state.apiProduct.apiProductsById
    ? Object.values(state.apiProduct.apiProductsById)
    : [];

  return {
    subscribeErrorMessage: state.apiProduct.subscribeErrorMessage,
    subscribedSuccessfully: state.apiProduct.subscribedSuccessfully,
    userId: getUserIdFromState(state),
    csrfToken: state.applicationUser?.token,
    allProducts: allProducts,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchSubscriptionStatus: (payload) => {
    dispatch(actions.fetchSubscriptionStatus(payload));
  },
  // Keep this to trigger changelog fetch via cycle
  fetchApiProductSuccess: (payload) => {
    dispatch(actions.fetchApiProductSuccess(payload));
  },
  clearSubscribeErrorMessage: () => {
    dispatch(actions.clearSubscribeErrorMessage());
  },
});

export default withTracker(
  AuthorizationHOC(ALL_USERS)(
    withBreadCrumb(
      connect(mapStateToProps, mapDispatchToProps)(ApiProductHOC),
      {
        parents: ["Products"],
        name: "{product}",
      }
    )
  )
);
